import React, { useCallback, useEffect, useState } from 'react';
import {
    CheckButton,
    CheckButtonFontStyle,
    DropDownBoxComponentSpacing,
    NumberBox,
    ShowIf,
    StateDropDown,
    TextBox,
    YearDropDown,
} from '@apply/components';
import {
    useBorrowerEntitiesQuery,
    useBusinessProfileQuery,
    useSaveBorrowerEntity,
} from '../queries/businessProfileQueries';
import { Address, BorrowerEntity, emptyAddress } from '../types';
import { AddressFields } from '../components/AddressFields';
import { YEAR_INCORPORATED_START } from '../constants/options';
import { StepStatus } from '../components/StepStatus';
import { RegisterStep } from '../wizard/stepApi';
import { asInput } from '../components/inputValue';
import { formatEin } from '../utils/formatEin';

interface BorrowerEntityStepProps {
    businessProfileId: number | null;
    activeEntityId: number | null;
    onRegister: RegisterStep;
}

const emptyEntity = (businessProfileId: number): BorrowerEntity => ({
    id: 0,
    businessProfileId,
    legalName: '',
    ownershipPercent: null,
    ein: '',
    yearIncorporated: null,
    stateIncorporated: '',
    addressSameAsBusinessProfile: true,
    address: emptyAddress(),
    lastEditedUtc: null,
});

export const BorrowerEntityStep: React.FC<BorrowerEntityStepProps> = ({
    businessProfileId,
    activeEntityId,
    onRegister,
}) => {
    const { data: profile } = useBusinessProfileQuery();
    const { data: entities, isLoading, isError, error, refetch } = useBorrowerEntitiesQuery(businessProfileId);
    const { mutateAsync: saveEntity } = useSaveBorrowerEntity();

    const [form, setForm] = useState<BorrowerEntity>(() => emptyEntity(businessProfileId ?? 0));
    const [isDirty, setIsDirty] = useState(false);

    // Seed from the server, but never on top of edits the user has not saved yet
    useEffect(() => {
        if (!entities || isDirty) {
            return;
        }
        const existing = entities.find((e) => e.id === activeEntityId) ?? entities[0];
        setForm(existing ?? emptyEntity(businessProfileId ?? 0));
    }, [entities, activeEntityId, businessProfileId, isDirty]);

    const save = useCallback(async () => {
        if (!isDirty) {
            return;
        }
        await saveEntity(form);
        setIsDirty(false);
    }, [form, isDirty, saveEntity]);

    useEffect(() => {
        onRegister({ save, isDirty });
    }, [save, isDirty, onRegister]);

    const update = <K extends keyof BorrowerEntity>(key: K, value: BorrowerEntity[K]) => {
        setIsDirty(true);
        setForm((prior) => ({ ...prior, [key]: value }));
    };

    const updateAddress = (patch: Partial<Address>) => {
        setIsDirty(true);
        setForm((prior) => ({ ...prior, address: { ...prior.address, ...patch } }));
    };

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className='bp-step'>
            <div className='pageSubHeader'>Add the first Borrower Entity you plan to use</div>

            <TextBox
                id='bpEntityLegalName'
                value={asInput(form.legalName)}
                onChange={(value) => update('legalName', value)}
                label='Legal Entity Name'
                required={true}
                maxLength={100}
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ flex: '1 1 140px', minWidth: '140px' }}>
                    <NumberBox
                        id='bpEntityOwnership'
                        value={form.ownershipPercent ?? undefined}
                        onChange={(value) => update('ownershipPercent', value)}
                        label='Ownership'
                        required={true}
                        format='#0'
                        min={1}
                        max={100}
                        validateValue={(value) => Number(value) > 0}
                        validateValueMessage='Ownership must be greater than 0'
                    />
                </div>
                <div style={{ flex: '1 1 272px', minWidth: '272px' }}>
                    <TextBox
                        id='bpEntityEin'
                        value={asInput(form.ein.replace(/\D/g, ''))}
                        onChange={(value: string) => update('ein', formatEin(value))}
                        label='EIN / Tax Identification'
                        required={true}
                        mask='00-0000000'
                        maskInvalidMessage='EIN must be 9 digits'
                    />
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                    <YearDropDown
                        id='bpEntityYearIncorporated'
                        value={form.yearIncorporated ?? undefined}
                        onChange={(value) => update('yearIncorporated', value ? Number(value) : null)}
                        label='Year Incorporated'
                        required={true}
                        yearStart={YEAR_INCORPORATED_START}
                    />
                </div>
                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                    <StateDropDown
                        id='bpEntityStateIncorporated'
                        value={asInput(form.stateIncorporated)}
                        onChange={(value) => update('stateIncorporated', value)}
                        label='State Incorporated'
                        required={true}
                        componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                    />
                </div>
            </div>

            <div style={{ marginTop: 20 }}>
                <CheckButton
                    id='bpEntityAddressSame'
                    fontStyle={CheckButtonFontStyle.Large}
                    label='Address is the same as Business Profile mailing address'
                    selected={form.addressSameAsBusinessProfile}
                    onToggle={() =>
                        update('addressSameAsBusinessProfile', !form.addressSameAsBusinessProfile)
                    }
                />
            </div>

            <ShowIf showIf={!form.addressSameAsBusinessProfile}>
                <div
                    className='pageSubHeader'
                    style={{ marginTop: 30 }}
                >
                    Entity Address
                </div>
                <AddressFields
                    idPrefix='bpEntity'
                    value={form.address}
                    requiredFields
                    onChange={(patch: Partial<Address>) => updateAddress(patch)}
                />
            </ShowIf>

            <ShowIf showIf={form.addressSameAsBusinessProfile && !!profile}>
                <div className='bp-step__hint'>
                    {profile?.mailingAddress.street1}
                    {profile?.mailingAddress.street1 && <br />}
                    {profile?.mailingAddress.city}
                    {profile?.mailingAddress.city && ', '}
                    {profile?.mailingAddress.state} {profile?.mailingAddress.zipCode}
                </div>
            </ShowIf>
        </div>
    );
};
